import ProductDescription, {
  VariantTypeSet
} from 'components/modules/e-commerce/ProductDescription';
import ProductDetailsTab from 'components/modules/e-commerce/ProductDetailsTab';
import { topElectronicProducts } from 'data/e-commerce/products';
import SimilarProducts from 'components/sliders/SimilarProducts';
import Section from 'components/base/Section';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { ecomBreadcrumbItems } from 'data/commonData';
import { useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import {
  getAllProducts,
  getProductByslug,
  Product,
  ProductDetail,
  ProductVariant
} from 'api/products/_requests';
import { PF } from 'helpers/utils';
import { addRecentView } from 'api/user/_requests';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';
import { useQueryClient } from '@tanstack/react-query';
import { Container } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { VendorCoupons } from 'api/coupon/_requests';

const ProductDetails = () => {
  const { slug } = useParams();
  const { user } = useSelector((state: RootState) => state.auth);
  const [product, setProduct] = useState<ProductDetail>({
    _id: '',
    vendorId: '',
    name: '',
    slug: '',
    description: '',
    thumbnail: '',
    tags: [],
    variantTypes: [],
    variants: [],
    productVariantId: '',
    keyfeature: '',
    inBox: [],
    specification: [],
    bulkOffer: [],
    coupons: []
  });
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant>();
  const [variantTypes, setVariantTypes] = useState<VariantTypeSet>({
    color: [],
    size: [],
    weight: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const saveRecentView = useCallback(
    async (slug: string, userId?: string) => {
      if (!slug) return;

      if (userId) {
        const payload = { userId, slug };
        try {
          await addRecentView(payload);
          queryClient.invalidateQueries({ queryKey: ['recent-Products'] });
        } catch (error) {
          console.error('Failed to save recent view:', error);
        }
      } else {
        try {
          const existing: string[] = JSON.parse(
            localStorage.getItem('recentProducts') || '[]'
          );

          const normalizedId = String(slug);
          const filtered = existing.filter(id => id !== normalizedId);
          const updated = [normalizedId, ...filtered].slice(0, 10);

          localStorage.setItem('recentProducts', JSON.stringify(updated));
        } catch (err) {
          console.error('LocalStorage error:', err);
        }
      }
    },
    [queryClient]
  );

  useEffect(() => {
    if (slug) {
      saveRecentView(slug, user?._id);
    }
  }, [slug, user?._id, saveRecentView]);

  const processProductData = useCallback((response: ProductDetail) => {
    const processedProduct = {
      ...response,
      thumbnail: PF + (response.thumbnail || 'goods'),
      variants: response.variants.map(variant => ({
        ...variant,
        images: [
          PF + (response.thumbnail || 'goods'),
          ...variant.images.map(image => PF + (image || 'goods'))
        ]
      }))
    };

    const colorSet = new Set<string>();
    const sizeSet = new Set<string>();
    const weightSet = new Set<string>();

    response.variants.forEach(variant => {
      if (variant.color) colorSet.add(variant.color);
      if (variant.size) sizeSet.add(variant.size);
      if (variant.weight) weightSet.add(variant.weight);
    });

    const variantTypes = {
      color: Array.from(colorSet),
      size: Array.from(sizeSet),
      weight: Array.from(weightSet)
    };

    return { processedProduct, variantTypes };
  }, []);

  useEffect(() => {
    if (!slug) {
      toast.error('Product ID is required');
      setError('Product ID is required');
      setIsLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await getProductByslug(slug);
        if (!response) {
          throw new Error('Product not found');
        }
        const { processedProduct, variantTypes } = processProductData(response);

        const coupon = setProduct(processedProduct);
        setSelectedVariant(processedProduct.variants?.[0]);
        setVariantTypes(variantTypes);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        setError(
          error instanceof Error ? error.message : 'Failed to load product'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [slug, processProductData]);

  // Loading state
  if (isLoading) {
    return (
      <div className="pt-5 mb-9">
        <Container className="px-xl-0 px-xxl-3">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading product details...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-5 mb-9">
        <Container className="px-xl-0 px-xxl-3">
          <div className="text-center py-5">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error</h4>
              <p>{error}</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!product._id) {
    return (
      <div className="pt-5 mb-9">
        <Container className="px-xl-0 px-xxl-3">
          <div className="text-center py-5">
            <p>Product not found</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="pt-5 mb-9">
      <Container className="px-xl-0 px-xxl-3">
        <div className="mb-9">
          <ProductDescription
            product={product}
            selectedVariant={selectedVariant ?? product?.variants?.[0]}
            setSelectedVariant={setSelectedVariant}
            variantTypes={variantTypes}
            productId={product._id}
          />
        </div>
      </Container>
    </div>
  );
};

export default ProductDetails;
